import { Table } from "react-bootstrap";
import { HistoricalTableKey } from "../../redux/historicalSlice";
import { AppTableRowActions } from "./AppTableRowActions";
import AppTablePagination from "./AppTablePagination";
import { SectionPermissionTypeEnum } from "../../types/SectionPermissionTypesEnum";
import { useUserPermissions } from "../../utils/auth";

export interface ColumnConfig<T> {
    header: string;
    key: keyof T | string;
    render?: (item: T) => React.ReactNode;
    headerClassName?: string;
    cellClassName?: string;
    cellStyle?: React.CSSProperties;
}

interface BaseTableProps<T> {
    data: T[];
    columns: ColumnConfig<T>[];
    rowStyle?: (item: T) => React.CSSProperties;
    emptyMessage: string;
    hasActions: boolean;
    sectionPermission?: SectionPermissionTypeEnum;
    onExportCsv?: () => void;
    onDelete?: (item: T) => void;
    canDelete?: (item: T) => boolean;
    onEnd?: (item: T) => void;
    canEnd?: (item: T) => boolean;
    onRestore?: (item: T) => void;
    canRestore?: (item: T) => boolean;
    onEdit?: (id: number) => void;
    onEditItem?: (item: T) => void;
    canEdit?: (item: T) => boolean;
    onView?: (id: number) => void;
    canView?: (item: T) => boolean;
    onActive?: (object: T) => void;
    canActive?: (object: T) => boolean;
    onSurveys?: (item: T) => void;
    onCopy?: (item: T) => void;
    t: (key: string) => string;
    showImportLink?: boolean;
    onImportCsv?: () => void;
    onDownloadTemplate?: () => void;
    downloadTemplateText?: string;
    activeCondition?: (object: T) => boolean;
    deleteCondition?: (object: T) => boolean;
}

interface NoPaginationProps {
    hasPagination?: false;
    currentPage?: never;
    totalPages?: never;
    pageSize?: never;
    paginationType?: never;
}

interface WithPaginationProps {
    hasPagination: true;
    currentPage: number;
    totalPages: number;
    pageSize: number;
    paginationType: HistoricalTableKey;
}

type AppTableProps<T> = BaseTableProps<T> & (NoPaginationProps | WithPaginationProps);

export default function AppTable<T extends { id: any }>({
    data,
    columns,
    rowStyle,
    emptyMessage,
    currentPage,
    totalPages,
    pageSize,
    hasPagination,
    paginationType,
    hasActions,
    sectionPermission,
    onExportCsv,
    onDelete,
    canDelete,
    onView,
    canView,
    onEnd,
    canEnd,
    onRestore,
    canRestore,
    onEdit,
    onEditItem,
    canEdit,
    onActive,
    canActive,
    onSurveys,
    onCopy,
    t,
    showImportLink,
    onImportCsv,
    onDownloadTemplate,
    downloadTemplateText,
    activeCondition,
    deleteCondition
}: AppTableProps<T>) {
    const totalColumns = columns.length + (hasActions ? 1 : 0);
    const permissions = useUserPermissions();

    return (
        <div className="w-100">
            <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
                <Table className="mt-3 text-center mb-0" style={{ width: "100%" }}>
                    <thead>
                        <tr>
                        {columns.map((col, idx) => (
                            <th key={idx} className={col.headerClassName} style={{ borderTop: "none", borderBottom: "1px solid black" }}>
                                {col.header}
                            </th>
                        ))}
                        {(hasActions) && (
                            <th style={{ minWidth: "120px", borderTop: "none", borderBottom: "1px solid black" }}>
                                {t("common:actions")}
                            </th>
                        )}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length > 0 ? (
                        data.map((item, rowIdx) => (
                            <tr key={item.id ? `id-${item.id}` : `row-${rowIdx}`}>
                                {columns.map((col, colIdx) => (
                                    <td
                                        key={colIdx}
                                        className={col.cellClassName}
                                        style={{ ...(rowStyle ? rowStyle(item) : {}), ...col.cellStyle }}
                                    >
                                        {col.render ? col.render(item) : (item[col.key as keyof T] as any) ?? "-"}
                                    </td>
                                ))}
                                {(hasActions) && (
                                    <td style={rowStyle ? rowStyle(item) : undefined}>
                                        <AppTableRowActions<T>
                                            object={item}
                                            onDelete={onDelete}
                                            canDelete={canDelete}
                                            onView={onView}
                                            canView={canView}
                                            onEnd={onEnd}
                                            canEnd={canEnd}
                                            onRestore={onRestore}
                                            canRestore={canRestore}
                                            onEdit={onEdit}
                                            onEditItem={onEditItem}
                                            canEdit={canEdit}
                                            onSurveys={onSurveys}
                                            onCopy={onCopy}
                                            onActive={onActive}
                                            canActive={canActive}
                                            t={t}
                                            sectionPermission={sectionPermission}
                                            activeCondition={activeCondition}
                                            deleteCondition={deleteCondition}
                                        />
                                    </td>
                                )}
                            </tr>
                        ))
                        ) : (
                        <tr>
                            <td colSpan={totalColumns}>{emptyMessage}</td>
                        </tr>
                    )}
                    </tbody>
                </Table>
            </div>
            {hasPagination && (
                <AppTablePagination
                    currentPage={currentPage!}
                    totalPages={totalPages!}
                    pageSize={pageSize!}
                    showExportLink={!!onExportCsv && sectionPermission && permissions.includes(`${SectionPermissionTypeEnum[sectionPermission]}.EXPORT`)}
                    onExportCsv={onExportCsv}
                    showImportLink={showImportLink}
                    onImportCsv={onImportCsv}
                    selectorType={paginationType!}
                    onDownloadTemplate={onDownloadTemplate}
                    downloadTemplateText={downloadTemplateText}
                    asTableRow={false}
                />
            )}
        </div>
    );
}
